import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { PartnerCityId } from '../../content';
import { portalCopy } from '../copy';
import { isValidMapsUrlInput, placesClient } from '../places/client';
import type { PlaceDetails, PlaceSuggestion } from '../places/types';
import { PortalButton, PortalField, portalInputClass } from './PortalUi';

type GooglePlaceSearchProps = {
  cityId?: PartnerCityId;
  onResolved: (place: PlaceDetails) => void;
};

function GooglePinIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden className="shrink-0 text-chisme-primary">
      <path
        d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7Z"
        fill="currentColor"
        fillOpacity="0.15"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <circle cx="12" cy="9" r="2.5" fill="currentColor" />
    </svg>
  );
}

const GooglePlaceSearch: React.FC<GooglePlaceSearchProps> = ({ cityId, onResolved }) => {
  const listboxId = useId();
  const searchRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<number | null>(null);

  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const [mapsUrl, setMapsUrl] = useState('');
  const [isResolvingLink, setIsResolvingLink] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);

  const runAutocomplete = useCallback(
    async (value: string) => {
      if (value.trim().length < 2) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      setIsSearching(true);
      setSearchError(null);

      try {
        const results = await placesClient.autocomplete({ query: value, cityId });
        setSuggestions(results);
        setShowSuggestions(results.length > 0);
        setHighlightIndex(-1);
      } catch {
        setSearchError(portalCopy.wizard.basics.google.searchError);
        setSuggestions([]);
        setShowSuggestions(false);
      } finally {
        setIsSearching(false);
      }
    },
    [cityId],
  );

  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);

    debounceRef.current = window.setTimeout(() => {
      void runAutocomplete(query);
    }, 280);

    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [query, runAutocomplete]);

  const selectSuggestion = async (suggestion: PlaceSuggestion) => {
    setQuery(suggestion.primaryText);
    setShowSuggestions(false);
    setSuggestions([]);
    setIsSearching(true);
    setSearchError(null);

    try {
      const details = await placesClient.getPlaceDetails(suggestion.placeId);
      if (!details) {
        setSearchError(portalCopy.wizard.basics.google.notFound);
        return;
      }
      onResolved(details);
    } catch {
      setSearchError(portalCopy.wizard.basics.google.searchError);
    } finally {
      setIsSearching(false);
    }
  };

  const handleResolveLink = async () => {
    const trimmed = mapsUrl.trim();
    if (!isValidMapsUrlInput(trimmed)) {
      setLinkError(portalCopy.wizard.basics.google.invalidLink);
      return;
    }

    setIsResolvingLink(true);
    setLinkError(null);

    try {
      const details = await placesClient.resolveMapsUrl(trimmed);
      if (!details) {
        setLinkError(portalCopy.wizard.basics.google.notFound);
        return;
      }
      onResolved(details);
    } catch {
      setLinkError(portalCopy.wizard.basics.google.searchError);
    } finally {
      setIsResolvingLink(false);
    }
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightIndex((prev) => (prev + 1) % suggestions.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightIndex((prev) => (prev <= 0 ? suggestions.length - 1 : prev - 1));
    } else if (event.key === 'Enter' && highlightIndex >= 0) {
      event.preventDefault();
      void selectSuggestion(suggestions[highlightIndex]);
    } else if (event.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const isLoading = isSearching || isResolvingLink;

  return (
    <div className="space-y-5">
      <div className="relative">
        <PortalField
          label={portalCopy.wizard.basics.google.searchLabel}
          htmlFor="google-place-search"
          hint={portalCopy.wizard.basics.google.searchHint}
        >
          <div className="relative">
            <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2">
              <GooglePinIcon />
            </span>
            <input
              ref={searchRef}
              id="google-place-search"
              type="search"
              autoComplete="off"
              role="combobox"
              aria-expanded={showSuggestions}
              aria-controls={listboxId}
              aria-autocomplete="list"
              className={`${portalInputClass} pl-11 pr-10`}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => {
                if (suggestions.length > 0) setShowSuggestions(true);
              }}
              onBlur={() => {
                window.setTimeout(() => setShowSuggestions(false), 150);
              }}
              onKeyDown={handleSearchKeyDown}
              placeholder={portalCopy.wizard.basics.google.searchPlaceholder}
            />
            {isSearching && (
              <span
                className="absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin rounded-full border-2 border-chisme-primary/25 border-t-chisme-primary"
                aria-hidden
              />
            )}
          </div>
        </PortalField>

        {searchError && (
          <p className="mt-2 font-jakarta text-sm text-red-600" role="alert">
            {searchError}
          </p>
        )}

        {showSuggestions && suggestions.length > 0 && (
          <ul
            id={listboxId}
            role="listbox"
            className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-2xl border border-[rgba(74,33,71,0.12)] bg-white py-1 shadow-lg"
          >
            {suggestions.map((suggestion, index) => (
              <li key={suggestion.placeId} role="option" aria-selected={index === highlightIndex}>
                <button
                  type="button"
                  className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-[#fcf7fa] ${
                    index === highlightIndex ? 'bg-[#fcf7fa]' : ''
                  }`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => void selectSuggestion(suggestion)}
                >
                  <GooglePinIcon />
                  <span className="min-w-0">
                    <span className="block font-jakarta text-sm font-semibold text-[#2a0f2a]">
                      {suggestion.primaryText}
                    </span>
                    <span className="mt-0.5 block font-jakarta text-xs text-[#5a3158]">
                      {suggestion.secondaryText}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex items-center gap-3">
        <span className="h-px flex-1 bg-[rgba(74,33,71,0.12)]" aria-hidden />
        <span className="font-jakarta text-xs font-semibold uppercase tracking-wide text-[#5a3158]">
          {portalCopy.wizard.basics.google.linkDivider}
        </span>
        <span className="h-px flex-1 bg-[rgba(74,33,71,0.12)]" aria-hidden />
      </div>

      <PortalField
        label={portalCopy.wizard.basics.google.linkLabel}
        htmlFor="google-maps-link"
        hint={portalCopy.wizard.basics.google.linkHint}
      >
        <div className="flex flex-col gap-3 sm:flex-row">
          <input
            id="google-maps-link"
            className={portalInputClass}
            value={mapsUrl}
            onChange={(e) => {
              setMapsUrl(e.target.value);
              setLinkError(null);
            }}
            placeholder={portalCopy.wizard.basics.google.linkPlaceholder}
            disabled={isLoading}
          />
          <PortalButton
            variant="secondary"
            className="shrink-0 sm:px-8"
            disabled={!mapsUrl.trim() || isResolvingLink}
            onClick={() => void handleResolveLink()}
          >
            {isResolvingLink
              ? portalCopy.wizard.basics.google.importing
              : portalCopy.wizard.basics.google.importLink}
          </PortalButton>
        </div>
      </PortalField>

      {linkError && (
        <p className="font-jakarta text-sm text-red-600" role="alert">
          {linkError}
        </p>
      )}

      {USE_MOCK_HINT && (
        <p className="rounded-xl bg-[#fcf7fa] px-4 py-3 font-jakarta text-xs leading-relaxed text-[#5a3158]">
          {portalCopy.wizard.basics.google.mockHint}
        </p>
      )}
    </div>
  );
};

/** Shown while Places API mock is active — remove when backend is live. */
const USE_MOCK_HINT = true;

export default GooglePlaceSearch;
